import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useState } from 'react'

import { ApiError } from '@/shared/api'

/**
 * Server state lives entirely in TanStack Query; nothing about a response is
 * copied into component state, so there is one source of truth per resource.
 */
function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry(failureCount, error) {
          // A contract or auth failure will not fix itself on a retry, and a
          // 429 has its own retry_after_seconds the caller must honour.
          if (error instanceof ApiError) {
            if (error.status === 429) return false
            if (error.status >= 400 && error.status < 500) return false
          }
          return failureCount < 2
        },
      },
      mutations: { retry: false },
    },
  })
}

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient)
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}
